import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { getToastRoot } from '../ToastPortal';

/**
 * La couche d'empilement des notifications.
 *
 * Un toast était à z-9999 et les dialogues montent jusqu'à 12020 : le message
 * passait donc SOUS l'assombrissement du dialogue, illisible au moment précis
 * où il comptait le plus — juste après une action lancée depuis ce dialogue.
 *
 * Cette constante est la seule valeur à faire évoluer si un écran devait un
 * jour monter plus haut. Un toast doit rester au-dessus de tout : il annonce
 * le résultat de ce que la personne vient de faire.
 */
export const COUCHE_TOAST = 'z-[13000]';

/**
 * ⚠️ `duration` EST OPTIONNELLE ET VAUT 3000 : aucun des ~79 fichiers qui
 * emploient ce composant sans la passer ne change de comportement. C'est la
 * condition de ce changement.
 *
 * Elle existe pour les annonces qu'il faut avoir le temps de lire SANS avoir à
 * les fermer — « demande envoyée au Centre de Support », qui est le cas NORMAL
 * dès que `module.comptabilite` est actif : radiation.routes.js ne rend
 * `validation_required: false` que module INACTIF. Un bandeau persistant y
 * coûterait un clic de fermeture par élève radié, soit dix pour une cohorte.
 *
 * Le type `loading` sort de l'effet AVANT la minuterie : il n'est pas concerné
 * — et il n'a pas de croix non plus, donc il reste indéfiniment si le serveur
 * ne répond jamais. Défaut connu, hors de ce lot.
 */
export const Toast = ({ show = true, message, type = 'success', duration = 3000, onClose }) => {
  useEffect(() => {
    if (!show) return;
    // Ne pas fermer automatiquement pour le type loading
    if (type === 'loading') return;
    
    const timer = setTimeout(() => {
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
    // `duration` dans les dépendances : sans elle, deux messages successifs de
    // durées différentes garderaient la minuterie du premier.
  }, [duration, onClose, show, type]);

  if (!show) return null;

  // ⚠️ `pointer-events-none` : LA CARTE NE DOIT PAS BLOQUER CE QU'ELLE RECOUVRE.
  // Elle est en `top-4 right-4` à z-[13000], soit exactement par-dessus les
  // contrôles droits de l'en-tête (cloche, messagerie, profil, Déconnexion) qui
  // vivent en z-50 : pendant toute la durée du toast, ces boutons ne répondaient
  // pas. Trois secondes passaient inaperçues ; huit ne passeraient pas.
  //
  // Sans danger : cette carte ne porte AUCUN élément cliquable — ni croix, ni
  // `onClick`. `LongToast` procède déjà ainsi (conteneur `pointer-events-none`,
  // carte `pointer-events-auto`), parce que lui a une croix.
  const baseClasses = "pointer-events-none fixed top-4 right-4 p-4 rounded-lg shadow-lg transform transition-all duration-500 ease-in-out flex items-center space-x-3";
  
  const typeClasses = {
    success: "bg-gradient-to-r from-customTeal to-customBlue text-white",
    error: "bg-gradient-to-r from-customSalmon to-red-500 text-white",
    loading: "bg-gradient-to-r from-customTeal to-customBlue text-white", // ⬅️ AJOUTÉ
    info: "bg-gradient-to-r from-customBlue to-blue-500 text-white" // ⬅️ AJOUTÉ (optionnel)
  };

  const normalizedType = typeClasses[type] ? type : 'info';

  // Rendu dans le conteneur dédié, jamais à l'endroit où il est appelé : sans
  // cela, un toast déclenché depuis un écran imbriqué reste enfermé dans le
  // contexte d'empilement de ses parents.
  return ReactDOM.createPortal((
    <div className={`${COUCHE_TOAST} ${baseClasses} ${typeClasses[normalizedType]} animate-slide-in`}>
      <div className="bg-white/20 rounded-full p-1">
        {normalizedType === 'success' && (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
        {normalizedType === 'error' && (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
        {/* `Loader2` et non un <svg> fait main : « Icônes : Lucide React
            uniquement » (CLAUDE.md). */}
        {normalizedType === 'loading' && (
          <Loader2 className="w-6 h-6 animate-spin" />
        )}
        {/* ⬅️ AJOUTÉ : Icône info (optionnel) */}
        {normalizedType === 'info' && (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
      </div>
      <p className="font-medium text-sm">{message}</p>
      {/*
        ⚠️ UNE CROIX, POUR LE SEUL TYPE QUI NE SE FERME PAS TOUT SEUL.

        Le type « chargement » sort de l'effet avant que la minuterie soit posée,
        et c'est juste : un indicateur ne doit pas disparaître pendant que
        l'opération court. Mais sans croix, un serveur qui ne répond jamais —
        coupure réseau pendant une radiation — laissait « Radiation de X en
        cours… » à l'écran indéfiniment, et il fallait recharger la page.

        ⚠️ `pointer-events-auto` est indispensable : la carte est
        `pointer-events-none` pour ne pas bloquer les commandes de l'en-tête
        qu'elle recouvre, donc sans cela la croix serait décorative.

        Les autres types n'en ont pas : ils s'effacent d'eux-mêmes, et une croix
        y ajouterait une cible à viser sur un message qui part tout seul.
      */}
      {normalizedType === 'loading' && (
        <button
          type="button"
          onClick={() => onClose?.()}
          aria-label="Fermer"
          className="pointer-events-auto ml-1 shrink-0 rounded-full p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  ), getToastRoot());
};